/**
 * @license
 * Copyright (c) 2016 The Polymer Project Authors. All rights reserved.
 * This code may only be used under the BSD style license found at http://polymer.github.io/LICENSE.txt
 * The complete set of authors may be found at http://polymer.github.io/AUTHORS.txt
 * The complete set of contributors may be found at http://polymer.github.io/CONTRIBUTORS.txt
 * Code distributed by Google as part of the polymer project is also
 * subject to an additional IP rights grant found at http://polymer.github.io/PATENTS.txt
 */

import { create } from "lodash";

/**
 * This shim allows elements written in, or compiled to, ES5 to work on native
 * implementations of Custom Elements v1. It sets new.target to the value of
 * this.constructor so that the native HTMLElement constructor can access the
 * current under-construction element's definition.
 */
 (function () {
    if (
        // No Reflect, no classes, no need for shim because native custom elements
        // require ES2015 classes or Reflect.
        window.Reflect === undefined ||
        window.customElements === undefined ||
        // The webcomponentsjs custom elements polyfill doesn't require
        // ES2015-compatible construction (`super()` or `Reflect.construct`).
        window.customElements.polyfillWrapFlushCallback
    ) {
        return;
    }
    const BuiltInHTMLElement = HTMLElement;
    /**
     * With jscompiler's RECOMMENDED_FLAGS the function name will be optimized away.
     * However, if we declare the function as a property on an object literal, and
     * use quotes for the property name, then closure will leave that much intact,
     * which is enough for the JS VM to correctly set Function.prototype.name.
     */
    const wrapperForTheName = {
        'HTMLElement': /** @this {!Object} */ function HTMLElement() {
            return Reflect.construct(
                BuiltInHTMLElement, [], /** @type {!Function} */(this.constructor));
        }
    };
    window.HTMLElement = wrapperForTheName['HTMLElement'];
    HTMLElement.prototype = BuiltInHTMLElement.prototype;
    HTMLElement.prototype.constructor = HTMLElement;
    Object.setPrototypeOf(HTMLElement, BuiltInHTMLElement);
})();

// Create a class for the element
class BtnOverview extends HTMLElement {

    constructor() {

        // Always call super first in constructor
        super();

        const shadow = this.attachShadow({mode: 'open'});

        // button
        const btnWrapper = document.createElement('div');
        const btnText = document.createElement('span');

        // box
        const boxWrapper = document.createElement('div');
        const boxBtnText = document.createElement('span');
        const boxDescription = document.createElement('span');
        const boxBtn = document.createElement('button');
        const boxFooter = document.createElement('div');
        const boxThumb = document.createElement('img');

        // modal
        const modalWrapper = document.createElement('div');
        const modalVideo = document.createElement('iframe');
        const modalVideoBtnCloseText = document.createElement('span');

        // elements
        this._shadow = shadow;
        this._btnWrapper = btnWrapper;
        this._btnText = btnText;
        this._boxWrapper = boxWrapper;
        this._boxBtnText = boxBtnText;
        this._boxFooter = boxFooter;
        this._boxDescription = boxDescription;
        this._boxBtn = boxBtn;
        this._boxThumb = boxThumb;
        this._modalWrapper = modalWrapper;
        this._modalVideo = modalVideo;
        this._modalVideoBtnCloseText = modalVideoBtnCloseText;

        // configs
        this._delayInMilliseconds = 400;
        this._tool = this.getAttribute('data-tool') || '';
        this._SS_OVERVIEW_BOX_CLOSED = 'wc_overview_ ' + location.host.replaceAll('.', '_') + '_' + this._tool; // Session Storage
        this._SS_OVERVIEW_MORE_INFOS = 'wc_overview_more_infos';  // Session Storage

        // images
        this._SVG_CLOSE = 'https://office.builderall.com/internacional/public/web-components/img/close.svg';
        this._SVG_LEFT_ARROW = 'https://office.builderall.com/internacional/public/web-components/img/arrow-left.svg';
        this._THUMBNAIL = 'https://office.builderall.com/internacional/public/web-components/img/thumb.jpg';

        // rules
        this._FRANCHISE_BUILDERALL = '30877';
        this._whiteLabelId = this.getAttribute('data-wl-id') || this._FRANCHISE_BUILDERALL;
        /**
         * DO NOT SHOW OVERVIEW TO WHITE LABEL CUSTOMERS
         */
        if(this._whiteLabelId == this._FRANCHISE_BUILDERALL)
        {
          this._create();
          this._onLoad();
        }
    }

    connectedCallback() {
        // console.log('>>> connectedCallback')
    }

    static get observedAttributes() {
        return [
            'data-btn-text',
            'data-box-opened',
            'data-box-description',
            'data-box-button-text',
            'data-box-button-link',
            'data-box-thumb',
            'data-modal-opened',
            'data-modal-close-text',
            'data-modal-video-url'
        ];
    }

    /*
        Run this on console to test attribute change:

        document.getElementsByTagName('btn-overview')[0].setAttribute('data-btn-text', 'System Help');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-box-description', 'No other delivers more emails to the inbox, has more tools, features, and apps integrated. #Builderall');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-box-button-text', '< infos here> ');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-box-button-link', 'https://www.nasa.gov');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-box-button-link', '');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-box-thumb', 'https://image.shutterstock.com/image-vector/overview-colorful-typography-banner-260nw-1312707791.jpg');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-box-thumb', 'https://office.builderall.com/internacional/public/web-components/img/thumb.jpg');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-modal-close-text', '< close modal >');
        document.getElementsByTagName('btn-overview')[0].setAttribute('data-modal-video-url', 'https://videomng.builderall.com/embed/6HsifDsosw/?controls=1&allowpause=1');
    */
    attributeChangedCallback(name, oldValue, newValue) {

        let hasValue = !(/^\s*(false|null|0|)\s*$/i.test(newValue.trim()));
        switch (name) {

            case 'data-btn-text':

                this._btnText.textContent = hasValue ? newValue : '';
                this._boxBtnText.textContent = hasValue ? newValue : '';
                break;

            case 'data-box-opened':

                if (newValue == '1') {
                    this._showSecond(this._btnWrapper, this._boxWrapper, this._delayInMilliseconds);
                }
                break;

            case 'data-box-description':

                this._boxDescription.textContent = hasValue ? newValue : '';
                break;

            case 'data-box-button-text':

                this._boxBtn.innerHTML = hasValue ? newValue : '';
                break;

            case 'data-box-button-link':

                if(hasValue) {
                    this._boxBtn.style.display = 'block';
                    this._boxDescription.style.width = 'calc(65% - 16px';
                    localStorage.setItem(this._SS_OVERVIEW_MORE_INFOS, newValue);
                } else {
                    this._boxBtn.style.display = 'none';
                    this._boxDescription.style.width = '100%';
                }
                break;

            case 'data-box-thumb':

                this._boxThumb.src = hasValue ? newValue : this._THUMBNAIL;
                break;

            case 'data-modal-opened':

                if (newValue == '1') {
                    this._showModal(this._btnWrapper, this._boxWrapper, this._modalWrapper, this._delayInMilliseconds)
                }
                break;

            case 'data-modal-close-text':

                this._modalVideoBtnCloseText.textContent = hasValue ? newValue : '';
                break;

            case 'data-modal-video-url':

                this._modalVideo.src = hasValue ? newValue : '';
                break;

            default:
                break;
        }
    }

    _onLoad() {

        let lsClosedBox = localStorage.getItem(this._SS_OVERVIEW_BOX_CLOSED) || '0';
        if (lsClosedBox == '0') {
            this._showSecond(this._btnWrapper, this._boxWrapper, this._delayInMilliseconds)
            localStorage.setItem(this._SS_OVERVIEW_BOX_CLOSED, '1');
        }

        // force the behavior
        // if(this.getAttribute('data-box-opened') != '1') {
        //     this._showSecond(this._btnWrapper, this._boxWrapper, this._delayInMilliseconds);
        // }
    }

    _showFirst(btnWrapper, boxWrapper, delayInMilliseconds, ssOverviewBoxClosed) {
        boxWrapper.style.animationName = 'desapear';
        setTimeout(function () {
            boxWrapper.style.display = 'none';
            boxWrapper.style.right = '-450px';
            btnWrapper.style.display = 'flex';
        }, delayInMilliseconds);

        localStorage.setItem(ssOverviewBoxClosed, '1');
    }

    _showSecond(btnWrapper, boxWrapper, delayInMilliseconds) {
        boxWrapper.classList.add("active");
        btnWrapper.style.display = 'none';
        boxWrapper.style.display = 'flex';
        boxWrapper.style.animationName = 'appear';
        setTimeout(function () {
            boxWrapper.style.right = '0';
        }, delayInMilliseconds);
    }

    _renderBoxButton(link) {
        if (!!link) {
            this._boxBtn.setAttribute('class', 'more_info');
            this._boxBtn.setAttribute('type', 'button');
            this._boxFooter.appendChild(this._boxBtn);
            this._boxBtn.addEventListener('click', () => {this._onClickBoxBtn(this._SS_OVERVIEW_MORE_INFOS);});
        } else {
            this._boxDescription.style.width = '100%';
        }
    }

    _onClickBoxBtn(key) {
        let link = localStorage.getItem(key);
        window.open(link, '_blank');
    }

    _showModal(btnWrapper, boxWrapper, modalWrapper, delayInMilliseconds) {
        boxWrapper.style.animationName = 'desapear';
        setTimeout(function () {
            btnWrapper.style.display = 'none';
            boxWrapper.style.display = 'none';
            boxWrapper.style.right = '-450px';
            modalWrapper.style.display = 'flex';
        }, delayInMilliseconds);
    }

    _closeModal(btnWrapper, boxWrapper, modalWrapper, modalVideo, modalVideoUrl, delayInMilliseconds) {
        modalWrapper.style.display = 'none';
        modalVideo.src = modalVideoUrl;
        this._showSecond(btnWrapper, boxWrapper, delayInMilliseconds);
    }

    _create() {

        // Reset shadow root content
        // this._shadow.innerHTML = '';

        /**
         * <BUTTON OVERVIEW>
         */
        this._btnWrapper.setAttribute('class', 'overview');
        this._btnWrapper.addEventListener('click', () => {this._showSecond(this._btnWrapper, this._boxWrapper, this._delayInMilliseconds)});

        // Create div title
        const title = document.createElement('div');
        title.setAttribute('class', 'ov_title');
        title.appendChild(this._btnText);
        this._btnWrapper.appendChild(title);

        // Create div title arrow
        const arrow = document.createElement('div');
        arrow.setAttribute('class', 'arrow');
        this._btnWrapper.appendChild(arrow);

        const icon = document.createElement('img');
        icon.src = this._SVG_LEFT_ARROW;
        arrow.appendChild(icon);
        /**
         * </BUTTON OVERVIEW>
         */


        /**
         * <BOX>
         */
        // Create div boxWrapper
        this._boxWrapper.setAttribute('class', 'pp_overview');
        this._boxWrapper.addEventListener('click', () => {this._showFirst(this._btnWrapper, this._boxWrapper, this._delayInMilliseconds, this._SS_OVERVIEW_BOX_CLOSED)});

        const boxClose = document.createElement('div');
        boxClose.setAttribute('class', 'pp_o_btnClose');/* this elemment to use */
        this._boxWrapper.appendChild(boxClose);

        var boxWrapper = this._boxWrapper;
        var showFirst = this._showFirst;
        var btnWrapper = this._btnWrapper;
        var delayInMilliseconds = this._delayInMilliseconds;
        var SS_OVERVIEW_BOX_CLOSED = this._SS_OVERVIEW_BOX_CLOSED;
        window.addEventListener('click', function(e) {
          if(!e.target.closest('btn-overview')) {
            showFirst(btnWrapper, boxWrapper, delayInMilliseconds, SS_OVERVIEW_BOX_CLOSED);
          }
        });

        const boxTitle = document.createElement('div');
        boxTitle.setAttribute('class', 'ov_title');
        boxClose.appendChild(boxTitle);
        boxTitle.appendChild(this._boxBtnText);

        // Create div title arrow
        const boxArrow = document.createElement('div');
        boxArrow.setAttribute('class', 'arrow');
        boxClose.appendChild(boxArrow);

        const boxIcon = document.createElement('img');
        boxIcon.src = this._SVG_LEFT_ARROW;
        boxArrow.appendChild(boxIcon);

        const boxContent = document.createElement('div');
        boxContent.setAttribute('class', 'pp_content');

        // thumbnail img
        this._boxThumb.setAttribute('class', 'thumb');
        boxContent.appendChild(this._boxThumb);
        this._boxThumb.addEventListener('click', () => {this._showModal(this._btnWrapper, this._boxWrapper, this._modalWrapper, this._delayInMilliseconds);});

        // create line separator div
        const lineSeparetor = document.createElement('div');
        lineSeparetor.setAttribute('class', 'line_separator');
        boxContent.appendChild(lineSeparetor);

        this._boxFooter.setAttribute('class', 'pp_footer');
        boxContent.appendChild(this._boxFooter);

        this._boxDescription.setAttribute('class', 'description');
        this._boxFooter.appendChild(this._boxDescription);

        this._renderBoxButton(this.getAttribute('data-box-button-link'));

        this._boxWrapper.appendChild(boxContent);
        /**
         * </BOX>
         */


        /**
         * <MODAL>
         */
        this._modalWrapper.setAttribute('class', 'modal_overview');
        this._modalWrapper.setAttribute('id', 'modal_overview');

        const modalStructure = document.createElement('div');
        modalStructure.setAttribute('class', 'modal_structure');
        this._modalWrapper.appendChild(modalStructure);

        const btnCloseModal = document.createElement('button');
        btnCloseModal.setAttribute('class', 'close_video');
        btnCloseModal.setAttribute('id', 'close_video');
        modalStructure.appendChild(btnCloseModal);
        btnCloseModal.addEventListener('click', () => {this._closeModal(this._btnWrapper, this._boxWrapper, this._modalWrapper, this._modalVideo, this.getAttribute('data-modal-video-url'), this._delayInMilliseconds); });
        btnCloseModal.appendChild(this._modalVideoBtnCloseText);

        const btnCloseImg = document.createElement('img');
        btnCloseImg.src = this._SVG_CLOSE;
        btnCloseModal.appendChild(btnCloseImg);

        this._modalVideo.setAttribute('class', 'video');
        this._modalVideo.setAttribute('id', 'iframeURL');
        this._modalVideo.setAttribute('width', 'auto');
        this._modalVideo.setAttribute('height', 'auto');

        modalStructure.appendChild(this._modalVideo);
        /**
         * </MODAL>
         */


        // Create some CSS to apply to the shadow dom
        const style = document.createElement('style');
        style.textContent = this._styles();

        // Attach the created elements to the shadow dom
        this._shadow.appendChild(style);
        this._shadow.appendChild(this._btnWrapper);
        this._shadow.appendChild(this._boxWrapper);
        this._shadow.appendChild(this._modalWrapper);
    }

    _styles() {
        return `
            /*ANIMATIONS*/
            @keyframes appear {
                from   {right: -450px;}
                to {right: 0px;}
            }
            @keyframes desapear {
                from   {right: 0px;}
                to {right: -450px;}
            }
            /*OVERVIEW*/
            .overview {
                background-color: #006CFF;
                color: white;
                border-radius: 0px 4px 4px 0px;
                right: 0px;
                top: 50%;
                -webkit-writing-mode: vertical-lr;
                writing-mode: vertical-lr;
                transform: translateY(-50%) rotate(180deg);
                position: fixed;
                z-index: 5000;
                display: flex;
                transition: box-shadow 0.2s;
            }
            .overview:hover {
                cursor: pointer;
                transition: box-shadow 0.2s;
                box-shadow: 0 0 1em #006CFF;
            }
            .overview:hover > .ov_title {
                transition: padding 0.2s;
                padding: 21px 12px 21px 12px;
            }
            .overview:hover > .arrow {
                transition: padding 0.2s;
                padding: 11px 12px 11px 12px;
            }
            .ov_title {
                transition: padding 0.2s;
                padding: 20px 10px 20px 10px;
            }
            .ov_title > span {
                font-family: 'Overview Muli', 'Muli';
                font-size: 19px;
                font-weight: 500;
            }
            .arrow {
                transition: padding 0.2s;
                background: #3188FF;
                padding: 10px 10px 10px 10px;
                border-radius: 0 0 4px 0;
            }
            .overview > .arrow > img {
                transform: rotate(180deg);
            }
            /*PICTURE TO PICTURE OVERVIEW*/
            .pp_overview {
                width: 420px;
                /*height: 290px;*/
                background-color: white;
                color: white;
                border-radius: 4px;
                right: -450px;
                bottom: 0;
                animation-duration: .5s;
                margin: 15px 10px;
                position: fixed;
                z-index: 500000000;
                display: none;
                box-shadow: 0 0 2em #0000003d
            }
            .pp_overview > .pp_o_btnClose {
                width: 45px;
                /*height: 100%;*/
                border-radius: 0px 4px 4px 0px;
                background-color: #006CFF;
                display: inline-flex;
                justify-content: space-between;
                -webkit-writing-mode: vertical-lr;
                writing-mode: vertical-lr;
                transform: rotate(180deg);
                cursor: pointer;
            }
            .pp_overview > .pp_o_btnClose > .arrow {
                border-radius: 0 0 4px 0;
            }
            .pp_overview > .pp_o_btnClose > .ov_title {
                padding-top: 85px !important;
            }
            .pp_overview > .pp_content {
                width: auto;
                height: auto;
                min-width: calc(100% - 65px);
                max-width: calc(100% - 65px);
                padding: 10px;
                display: flex;
                flex-direction: column;
            }
            .pp_overview > .pp_content > .thumb {
                width: 350px;
                height: 185px;
                cursor: pointer;
            }
            .pp_overview > .pp_content > .line_separator {
                margin: 10px 7px 10px 5px;
                height: 1px;
                background-color: rgba(0, 0, 0, 0.09);
            }
            .pp_overview > .pp_content > .pp_footer {
                display: inline-flex;
                color: black;
                font-family: 'Overview Muli', 'Muli';
                font-size: 13px;
            }
            .pp_overview > .pp_content > .pp_footer > span {
                width: calc(65% - 16px);
                margin: auto 8px;
                text-align: justify;
            }
            .pp_overview > .pp_content > .pp_footer > button {
                width: calc(35% - 16px);
                height: 36px;
                margin: auto 8px;
                background: #0F7BFF;
                color: #fff;
                border-radius: 4px;
                border: none;
                font-family: 'Overview Muli', 'Muli';
                font-size: 14px;
                cursor: pointer;
            }
            /*MODAL OVERVIEW*/
            .modal_overview {
                top: 0 !important;
                left: 0 !important;
                width: 100vw;
                height: 100vh;
                background-color: #00000080;
                color: white;
                animation-duration: 1s;
                position: fixed;
                z-index: 500000000;
                display: none;
                align-items: center;
                justify-content: center;
            }
            .modal_overview > .modal_structure {
                width: 70%;
                height: 90vh;
                z-index: 5500;
                display: flex;
                flex-direction: column;
                align-items: flex-end;
            }
            .modal_overview > .modal_structure > button {
                width: 189px;
                height: 50px;
                background: #006CFF;
                border-radius: 4px;
                border: none;
                display: inline-flex;
                align-items: center;
                justify-content: space-evenly;
                cursor: pointer;
            }
            .modal_overview > .modal_structure > button > span {
                font-family: 'Overview Muli', 'Muli';
                color: #fff;
                font-size: 15px;
                font-weight: 500;
            }
            .modal_overview > .modal_structure > .video {
                width: calc(100% - 28px);
                height: calc(100% - 35px);
                background-color: #D7D1D1;
                margin-top: 8px;
                padding: 5px;
                border-radius: 4px;
                border: none
            }
            @media only screen and (max-device-width: 600px) {
                .pp_overview {
                    width: 320px!important;
                }
                .pp_overview > .pp_o_btnClose {
                    width: 35px!important;
                }
                .pp_overview > .pp_content > .thumb {
                    width: 260px!important;
                }
                .pp_overview > .pp_content > .pp_footer > span {
                    width: calc(65% - 4px)!important;
                    margin: auto 4px!important;
                }
                .pp_overview > .pp_content > .pp_footer {
                    font-size: 11px!important;
                }
                .pp_overview > .pp_content > .pp_footer > button {
                    width: calc(35% - 4px)!important;
                    margin: auto 4px!important;
                    font-size: 12px!important;
                }
                .modal_overview > .modal_structure {

                    width: 85%!important;

                }
            }
        `;
    }
}

// Load external css source
const style = document.createElement('style');
style.textContent = `
    /* vietnamese */
    @font-face {
      font-family: 'Overview Muli';
      font-style: normal;
      font-weight: 400;
      src: url(https://fonts.gstatic.com/s/muli/v22/7Aulp_0qiz-aVz7u3PJLcUMYOFnOkEk40eiNxw.woff2) format('woff2');
      unicode-range: U+0102-0103, U+0110-0111, U+0128-0129, U+0168-0169, U+01A0-01A1, U+01AF-01B0, U+1EA0-1EF9, U+20AB;
    }
    /* latin-ext */
    @font-face {
      font-family: 'Overview Muli';
      font-style: normal;
      font-weight: 400;
      src: url(https://fonts.gstatic.com/s/muli/v22/7Aulp_0qiz-aVz7u3PJLcUMYOFnOkEk50eiNxw.woff2) format('woff2');
      unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
    }
    /* latin */
    @font-face {
      font-family: 'Overview Muli';
      font-style: normal;
      font-weight: 400;
      src: url(https://fonts.gstatic.com/s/muli/v22/7Aulp_0qiz-aVz7u3PJLcUMYOFnOkEk30eg.woff2) format('woff2');
      unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
    }
   `;

document.head.appendChild(style);
// Define the new element
customElements.define('btn-overview', BtnOverview);



// WEBPACK FOOTER //
// ./resources/web-components/js/wc-tutorial-video.js